import { getAuthToken } from "@/lib/storage"
import { getSocketBaseUrl } from "@/lib/socketBase"
import { io, Socket } from "socket.io-client"
import {
  LocationMapRemovedPayload,
  MapPlayerPresence,
  MapPresenceHeartbeatBody,
} from "../types/location"
import { normalizeMapPlayer } from "../utils/mapPlayers"

const API_BASE = getSocketBaseUrl()

type LocationEmitEvents = {
  "location-heartbeat": (payload: MapPresenceHeartbeatBody) => void
  "location-leave": () => void
}

type LocationListenEvents = {
  "location-heartbeat-ack": (payload: {
    ok?: boolean
    expiresInSec?: number
    player?: unknown
  }) => void
  "location-map-updated": (payload: { player?: unknown }) => void
  "location-map-removed": (payload: LocationMapRemovedPayload) => void
  connect_error: (payload: { message?: string }) => void
  error: (payload: { message?: string }) => void
}

type RegisteredListener = {
  event: keyof LocationListenEvents
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handler: (...args: any[]) => void
}

let socket: Socket | null = null
let lastAuthToken: string | null = null
let connectPromise: Promise<Socket> | null = null
let connectPromiseToken: string | null = null

const registeredListeners: RegisteredListener[] = []

function normalizeAuthToken(raw: string | null | undefined): string {
  return raw?.replace(/^Bearer\s+/i, "").trim() ?? ""
}

function bindListener(sock: Socket, entry: RegisteredListener) {
  const s = sock as Socket & {
    on: (ev: string, fn: (...args: unknown[]) => void) => void
    off: (ev: string, fn: (...args: unknown[]) => void) => void
  }
  s.off(entry.event, entry.handler as (...args: unknown[]) => void)
  s.on(entry.event, entry.handler as (...args: unknown[]) => void)
}

function bindAllRegisteredListeners(sock: Socket) {
  for (const entry of registeredListeners) {
    bindListener(sock, entry)
  }
}

function createLocationSocket(token: string): Socket {
  const sock = io(`${API_BASE}/game/location`, {
    auth: { token },
    transports: ["websocket", "polling"],
    autoConnect: true,
    reconnection: true,
    reconnectionAttempts: Infinity,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
  })
  bindAllRegisteredListeners(sock)
  return sock
}

function clearConnectPromise(token: string) {
  if (connectPromiseToken === token) {
    connectPromise = null
    connectPromiseToken = null
  }
}

function teardownLocationSocket() {
  if (socket) {
    socket.removeAllListeners()
    socket.disconnect()
    socket = null
  }
}

function waitForSocketConnect(sock: Socket): Promise<Socket> {
  if (sock.connected) return Promise.resolve(sock)

  return new Promise((resolve, reject) => {
    const onConnect = () => {
      cleanup()
      resolve(sock)
    }
    const onError = (err: Error) => {
      cleanup()
      reject(err)
    }
    const cleanup = () => {
      sock.off("connect", onConnect)
      sock.off("connect_error", onError)
    }

    sock.once("connect", onConnect)
    sock.once("connect_error", onError)
    if (!sock.connected) {
      sock.connect()
    }
  })
}

async function connectLocationSocketInternal(token: string): Promise<Socket> {
  if (socket?.connected && lastAuthToken === token) {
    return socket
  }

  if (socket && lastAuthToken === token && !socket.connected) {
    return waitForSocketConnect(socket)
  }

  if (socket) {
    teardownLocationSocket()
  }

  lastAuthToken = token
  socket = createLocationSocket(token)
  return waitForSocketConnect(socket)
}

export async function connectLocationSocket(
  explicitToken?: string | null
): Promise<Socket> {
  const stored = explicitToken ?? getAuthToken()
  const token = normalizeAuthToken(stored)

  if (!token) {
    throw new Error("JWT required for /game/location")
  }

  if (socket?.connected && lastAuthToken === token) {
    return socket
  }

  if (connectPromise && connectPromiseToken === token) {
    return connectPromise
  }

  connectPromiseToken = token
  connectPromise = connectLocationSocketInternal(token)
    .catch((err) => {
      clearConnectPromise(token)
      throw err
    })
    .finally(() => {
      clearConnectPromise(token)
    })

  return connectPromise
}

export function disconnectLocationSocket() {
  connectPromise = null
  connectPromiseToken = null
  teardownLocationSocket()
  lastAuthToken = null
}

export function getLocationSocket(): Socket | null {
  return socket
}

export function emitLocationEvent<K extends keyof LocationEmitEvents>(
  event: K,
  ...args: Parameters<LocationEmitEvents[K]>
) {
  if (!socket?.connected) return false
  socket.emit(event, ...args)
  return true
}

export async function emitLocationEventWhenReady<K extends keyof LocationEmitEvents>(
  token: string | null | undefined,
  event: K,
  ...args: Parameters<LocationEmitEvents[K]>
): Promise<boolean> {
  try {
    await connectLocationSocket(token)
  } catch {
    return false
  }
  return emitLocationEvent(event, ...args)
}

export function onLocationEvent<K extends keyof LocationListenEvents>(
  event: K,
  handler: LocationListenEvents[K]
) {
  const entry: RegisteredListener = {
    event,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    handler: handler as (...args: any[]) => void,
  }
  registeredListeners.push(entry)

  if (socket) {
    bindListener(socket, entry)
  }

  return () => {
    const idx = registeredListeners.indexOf(entry)
    if (idx >= 0) registeredListeners.splice(idx, 1)
    ;(socket as Socket & { off: (ev: string, fn: (...args: unknown[]) => void) => void } | null)?.off(
      event,
      entry.handler as (...args: unknown[]) => void
    )
  }
}

export function normalizeLocationMapUpdatedPlayer(
  payload: unknown
): MapPlayerPresence | null {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return null
  }
  const obj = payload as Record<string, unknown>
  const nested = obj.player ?? obj.data ?? obj.payload
  return normalizeMapPlayer(nested ?? obj)
}

export function normalizeLocationMapRemovedPayload(
  payload: unknown
): LocationMapRemovedPayload | null {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    return null
  }
  const obj = payload as Record<string, unknown>
  const userId = Number(obj.userId ?? obj.user_id)
  if (!Number.isFinite(userId)) return null
  const reasonRaw = String(obj.reason ?? "TTL_EXPIRED").toUpperCase()
  const reason =
    reasonRaw === "DISCONNECTED" || reasonRaw === "LEFT"
      ? reasonRaw
      : "TTL_EXPIRED"
  return { userId, reason }
}
